import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { SelectorCuentaPago } from '../components/CuentaPago';
import { Badge, Spinner, Toast, css, Modal, PageHeader, EstadoBadge, IconBtn, opcionesEstado, Grid, Label, MontoInput, Paginacion, ESTADO_LABEL, DestinoPagoFijo, EstadoMenu, useDeshacer, ROL_COLORS, ESTADO_COLORS } from '../components/UI';
import { Icono, ConIcono } from '../components/Iconos';

const ESTADOS = ['pendiente', 'aprobada', 'por_pagar', 'rechazada', 'pagada'];

export default function HistorialComprobaciones() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [filtros, setFiltros] = useState({ estado: '', empresa: '', sucursal: '', comprobador: '', solicitante: '', desde: '', hasta: '', sortField: 'createdAt', sortDir: 'desc', page: 1 });
  const [personaInput, setPersonaInput] = useState('');
  const [solicInput, setSolicInput] = useState('');
  const [catalogos, setCatalogos] = useState({ empresas: [], sucursales: [] });
  // Centros con presupuesto rebasado este mes → marca de alerta en filas y detalle
  const [rebasados, setRebasados] = useState({});
  const alertaPresupuesto = (cc) => {
    const a = rebasados[cc];
    if (!a) return null;
    const f = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 });
    return a.razon === 'centro'
      ? `Presupuesto del centro "${cc}" rebasado este mes: $${f(a.gastado)} de $${f(a.presupuesto)} autorizados`
      : `Presupuesto de la dirección "${a.direccion}" rebasado este mes: $${f(a.gastado)} de $${f(a.presupuesto)} autorizados`;
  };
  const [rechazoModal, setRechazoModal] = useState({ open: false, id: null, motivo: '' });
  // Comprobante de pago obligatorio al marcar como pagada
  const [pagoModal, setPagoModal] = useState({ open: false, id: null, files: [], yaTiene: false, fecha: '', subiendo: false });
  const [progModal, setProgModal] = useState({ open: false, id: null, fecha: '' });
  // Corregir y reenviar una rechazada (solo el dueño o admin)
  const [corregirModal, setCorregirModal] = useState(null);
  const [comentario, setComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const comentarioRef = useRef(null);

  const { programar, aviso } = useDeshacer();
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(filtros).filter(([, v]) => v))).toString();
      setData(await api.get(`/comprobaciones?${qs}`));
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { load(); }, [load]);

  // Catálogos para los selects de empresa/sucursal (una sola vez)
  useEffect(() => {
    Promise.all([api.get('/empresas').catch(() => []), api.get('/sucursales').catch(() => [])])
      .then(([emp, suc]) => setCatalogos({ empresas: emp, sucursales: suc.map(s => s.nombre) }));
    api.get('/presupuestos/rebasados').then(d => setRebasados(d.afectados || {})).catch(() => {});
  }, []);

  // Búsqueda con debounce (400 ms tras dejar de escribir)
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltros(f => f.comprobador === personaInput ? f : { ...f, comprobador: personaInput, page: 1 });
    }, 400);
    return () => clearTimeout(t);
  }, [personaInput]);

  // Búsqueda por solicitante con debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltros(f => f.solicitante === solicInput ? f : { ...f, solicitante: solicInput, page: 1 });
    }, 400);
    return () => clearTimeout(t);
  }, [solicInput]);

  // Al cambiar de empresa, limpia la sucursal si no le pertenece
  const cambiarEmpresa = (v) => setFiltros(f => {
    const emp = catalogos.empresas.find(e => e.nombre === v);
    const sucValida = !v || !emp?.sucursales?.length || emp.sucursales.includes(f.sucursal);
    return { ...f, empresa: v, sucursal: sucValida ? f.sucursal : '', page: 1 };
  });

  const empresaObj = catalogos.empresas.find(e => e.nombre === filtros.empresa);
  const sucursalesVisibles = empresaObj?.sucursales?.length
    ? catalogos.sucursales.filter(s => empresaObj.sucursales.includes(s))
    : catalogos.sucursales;

  const cambiarEstado = async (id, estado, motivo = '', extra = {}) => {
    try {
      const comp = await api.patch(`/comprobaciones/${id}/estado`, { estado, motivo, ...extra });
      toast_(`Estado actualizado: ${ESTADO_LABEL[estado] || estado}`);
      if (detalle?._id === id) setDetalle(comp);
      load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const handleEstado = (id, estado) => {
    if (estado === 'rechazada') setRechazoModal({ open: true, id, motivo: '' });
    else if (estado === 'pagada') {
      const item = data.items.find(i => i._id === id);
      const yaTiene = (item?.comprobantes || []).some(a => a.concepto === 'Comprobante de pago');
      setPagoModal({ open: true, id, empresa: item?.empresa, cuentaPago: '', files: [], yaTiene, fecha: '', subiendo: false });
    }
    else if (estado === 'por_pagar') { const hoy = new Date().toLocaleDateString('en-CA'); setProgModal({ open: true, id, fecha: hoy }); }
    else {
      const item = data.items.find(i => i._id === id);
      programar(`${ESTADO_LABEL[estado] || estado} · ${item?.folio || ''}`, () => cambiarEstado(id, estado));
    }
  };

  const confirmarPago = async () => {
    const { id, files, yaTiene, fecha } = pagoModal;
    if (!files.length && !yaTiene) { toast_('Adjunta el comprobante de pago (PDF o imagen)', 'error'); return; }
    if (!fecha) { toast_('Indica la fecha en que se hizo el pago', 'error'); return; }
    if (fecha > new Date().toLocaleDateString('en-CA')) { toast_('La fecha de pago no puede ser futura', 'error'); return; }
    setPagoModal(m => ({ ...m, subiendo: true }));
    try {
      if (files.length) {
        const fd = new FormData();
        files.forEach(f => fd.append('archivos', f));
        fd.append('concepto', 'Comprobante de pago');
        await api.upload(`/comprobaciones/${id}/comprobantes`, fd);
      }
      await cambiarEstado(id, 'pagada', '', { fechaPago: fecha, cuentaPago: pagoModal.cuentaPago || undefined });
      setPagoModal({ open: false, id: null, files: [], yaTiene: false, fecha: '', subiendo: false });
    } catch (e) {
      toast_(e.message, 'error');
      setPagoModal(m => ({ ...m, subiendo: false }));
    }
  };

  const confirmarProg = async () => {
    const { id, fecha } = progModal;
    if (!fecha) { toast_('Indica la fecha de programación', 'error'); return; }
    await cambiarEstado(id, 'por_pagar', '', { fechaProgramacion: fecha });
    setProgModal({ open: false, id: null, fecha: '' });
  };

  const confirmarRechazo = async () => {
    if (!rechazoModal.motivo.trim()) { toast_('El motivo de rechazo es obligatorio', 'error'); return; }
    await cambiarEstado(rechazoModal.id, 'rechazada', rechazoModal.motivo);
    setRechazoModal({ open: false, id: null, motivo: '' });
  };

  const puedeCorregir = (s) => s.estado === 'rechazada' &&
    (user?.rol === 'admin' || String(s.usuario?._id || s.usuario) === String(user?._id));

  const abrirCorregir = (s) => setCorregirModal({
    id: s._id, folio: s.folio, motivoRechazo: s.motivoRechazo || '', nota: '', enviando: false,
    // El destino del pago se muestra pero no se edita ni se envía: sólo lectura
    destino: {
      comprobador: s.comprobador || '',
      banco: s.banco || '', clabe: s.clabe || '', cuenta: s.cuenta || '',
    },
    campos: {
      conceptoGasto: s.conceptoGasto || '', monto: String(s.monto ?? ''),
      folioCfdi: s.folioCfdi || '', observaciones: s.observaciones || '', direccion: s.direccion || '',
    },
  });

  const confirmarReenvio = async () => {
    const { id, campos, nota } = corregirModal;
    if (!(parseFloat(campos.monto) > 0)) { toast_('El monto es obligatorio', 'error'); return; }
    setCorregirModal(m => ({ ...m, enviando: true }));
    try {
      await api.post(`/comprobaciones/${id}/reenviar`, { ...campos, monto: parseFloat(campos.monto) || 0, nota });
      toast_('Comprobación corregida y reenviada: vuelve a estar pendiente de aprobación');
      setCorregirModal(null);
      load();
    } catch (e) { toast_(e.message, 'error'); setCorregirModal(m => ({ ...m, enviando: false })); }
  };

  const handlePDF = async (id, folio) => {
    try {
      const blob = await api.downloadPdf(`/comprobaciones/${id}/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `Comprobacion_${folio}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    if (!['admin', 'contador'].includes(user?.rol)) return;
    const motivo = window.prompt('Motivo de borrado (obligatorio):');
    if (motivo === null) return;
    if (!motivo.trim()) { toast_('El motivo de borrado es obligatorio', 'error'); return; }
    try { await api.del(`/comprobaciones/${id}`, { motivo: motivo.trim() }); toast_('Comprobación borrada'); load(); }
    catch (e) { toast_(e.message, 'error'); }
  };

  const abrirDetalle = (item) => {
    setDetalle(item);
    setComentario('');
    // Marca los comentarios como leídos y apaga la alerta de comentarios de la fila
    if (item.comentariosNuevos > 0) {
      api.post(`/comprobaciones/${item._id}/comentarios/visto`, {}).catch(() => {});
      setData(d => ({ ...d, items: d.items.map(i => i._id === item._id ? { ...i, comentariosNuevos: 0 } : i) }));
    }
  };

  const handleComentario = async () => {
    if (!comentario.trim() || !detalle) return;
    setEnviandoComentario(true);
    try {
      const r = await api.post(`/comprobaciones/${detalle._id}/comentarios`, { texto: comentario });
      setDetalle(d => ({ ...d, comentarios: r.comentarios }));
      setComentario('');
      if (comentarioRef.current) comentarioRef.current.focus();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setEnviandoComentario(false); }
  };

  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v, page: 1 }));

  const toggleSort = (field) => setFiltros(f => ({
    ...f, sortField: field,
    sortDir: f.sortField === field && f.sortDir === 'desc' ? 'asc' : 'desc',
    page: 1,
  }));

  const SortTh = ({ field, label, style = {} }) => {
    const active = filtros.sortField === field;
    return (
      <th onClick={() => toggleSort(field)} style={{
        padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700,
        color: active ? 'var(--plasma)' : 'var(--glass)', textTransform: 'uppercase',
        letterSpacing: '0.07em', whiteSpace: 'nowrap', cursor: 'pointer',
        userSelect: 'none', transition: 'color 0.12s', ...style,
      }}>
        {label} <span style={{ fontSize: 11.5, opacity: active ? 1 : 0.35 }}>{active ? (filtros.sortDir === 'desc' ? '▼' : '▲') : '⇅'}</span>
      </th>
    );
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      {aviso}

      <PageHeader
        icon="carpeta"
        title={user?.rol === 'solicitante' ? 'Mis Comprobaciones' : 'Todas las Comprobaciones'}
        subtitle={`${data.total} comprobación${data.total !== 1 ? 'es' : ''} encontrada${data.total !== 1 ? 's' : ''}`}
        actions={
          <button onClick={load} style={{ ...css.btnGhost, border: '1px solid var(--hair)', fontSize: 12 }}>
            <ConIcono nombre="refrescar">Actualizar</ConIcono>
          </button>
        }
      />

      {/* Filtros */}
      <div style={{ ...css.card, padding: 0, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ padding: '9px 18px', background: 'var(--panel)', borderBottom: '1px solid var(--hair)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em' }}><ConIcono nombre="buscar" size={13}>Filtros</ConIcono></span>
        </div>
        <div style={{ padding: '12px 18px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filtros.estado} onChange={e => setF('estado', e.target.value)} style={{ ...css.select(false), width: 150 }}>
            <option value="">Todos los estados</option>
            {ESTADOS.map(e => <option key={e} value={e}>{ESTADO_LABEL[e] || e}</option>)}
          </select>
          <select value={filtros.empresa} onChange={e => cambiarEmpresa(e.target.value)} style={{ ...css.select(false), width: 190 }}>
            <option value="">Todas las compañías</option>
            {catalogos.empresas.map(e => <option key={e._id || e.nombre} value={e.nombre}>{e.nombre}</option>)}
          </select>
          <select value={filtros.sucursal} onChange={e => setF('sucursal', e.target.value)} style={{ ...css.select(false), width: 180 }}>
            <option value="">{filtros.empresa ? `Sucursales de ${filtros.empresa}` : 'Todas las sucursales'}</option>
            {sucursalesVisibles.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={personaInput} onChange={e => setPersonaInput(e.target.value)}
            placeholder="Buscar comprobador..." style={{ ...css.input(false), width: 190 }} />
          {user?.rol !== 'solicitante' && (
            <input value={solicInput} onChange={e => setSolicInput(e.target.value)}
              placeholder="Buscar solicitante..." style={{ ...css.input(false), width: 190 }} />
          )}
          <input type="date" value={filtros.desde} onChange={e => setF('desde', e.target.value)} style={{ ...css.input(false), width: 148 }} />
          <input type="date" value={filtros.hasta} onChange={e => setF('hasta', e.target.value)} style={{ ...css.input(false), width: 148 }} />
          <button onClick={() => { setPersonaInput(''); setSolicInput(''); setFiltros({ estado: '', empresa: '', sucursal: '', comprobador: '', solicitante: '', desde: '', hasta: '', sortField: 'createdAt', sortDir: 'desc', page: 1 }); }}
            style={{ ...css.btn('secundario'), fontSize: 12 }}>Limpiar</button>
        </div>
      </div>

      {/* Tabla */}
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><Spinner /></div>
        ) : data.items.length === 0 ? (
          <div style={{ padding: '52px 32px', textAlign: 'center' }}>
            <div style={{ marginBottom: 12, color: 'var(--steel)' }}><Icono nombre="documento" size={28} /></div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-soft)' }}>Sin comprobaciones</div>
            <div style={{ fontSize: 12, color: 'var(--glass)', marginTop: 4 }}>Crea una nueva desde el menú</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
                <SortTh field="folio"        label="Folio" />
                <SortTh field="createdAt"    label="Fecha" />
                <SortTh field="empresa"      label="Compañía" />
                <SortTh field="comprobador"  label="Comprobador" />
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>Concepto</th>
                <SortTh field="monto"        label="Monto" />
                <SortTh field="estado"       label="Estado" />
                <th style={{ padding: '10px 14px' }}></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(item => (
                <tr key={item._id} style={{ borderBottom: '1px solid var(--hair-soft)', transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--plasma-dim)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 14px', fontFamily: 'var(--digit-font)', fontSize: 11.5, fontWeight: 600, color: 'var(--plasma)' }}>
                    <button onClick={() => abrirDetalle(item)} style={{ background: 'none', border: 'none', color: 'var(--plasma)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', padding: 0 }}>
                      {item.folio}
                    </button>
                    {item.comentariosNuevos > 0 && (
                      <span onClick={() => abrirDetalle(item)} title={`${item.comentariosNuevos} comentario(s) nuevo(s) — haz clic para leer`}
                        style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 99, fontSize: 11.5, fontWeight: 800, cursor: 'pointer', background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger-line)', whiteSpace: 'nowrap' }}>
                        <Icono nombre="comentario" size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />{item.comentariosNuevos} nuevo{item.comentariosNuevos > 1 ? 's' : ''}
                      </span>
                    )}
                    {rebasados[item.centroCostos] && (
                      <span title={alertaPresupuesto(item.centroCostos)}
                        style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 99, fontSize: 11.5, fontWeight: 800, background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger-line)', whiteSpace: 'nowrap', cursor: 'help' }}>
                        <Icono nombre="alerta" size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />Presupuesto
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '9px 14px', color: 'var(--glass)', fontSize: 12 }}>{item.fechaSolicitud || new Date(item.createdAt).toLocaleDateString('es-MX')}</td>
                  <td style={{ padding: '9px 14px', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><span style={{ fontWeight: 500, color: 'var(--ink)' }}>{item.empresa}</span></td>
                  <td style={{ padding: '9px 14px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink-soft)' }}>{item.comprobador}</td>
                  <td style={{ padding: '9px 14px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--glass)', fontSize: 12 }}>{item.conceptoGasto}</td>
                  <td style={{ padding: '9px 14px', fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', fontFamily: 'var(--digit-font)' }}>
                    ${(item.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>{item.moneda}</span>
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    {(() => {
                      const ops = opcionesEstado(user.rol, item);
                      return ops ? (
                        <EstadoMenu estado={item.estado} opciones={ops} onElegir={v => handleEstado(item._id, v)} />
                      ) : (
                        <EstadoBadge estado={item.estado} />
                      );
                    })()}
                    {item.estado === 'rechazada' && item.motivoRechazo && (
                      <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 2, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.motivoRechazo}>{item.motivoRechazo}</div>
                    )}
                    {item.fechaProgramacion && ['por_pagar','pagada'].includes(item.estado) && (
                      <div style={{ fontSize: 11.5, color: 'var(--info)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, fontFamily: 'var(--digit-font)' }}><Icono nombre="calendario" size={12} />{new Date(item.fechaProgramacion).toLocaleDateString('en-CA')}</div>
                    )}
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button onClick={() => abrirDetalle(item)} title="Ver detalle" aria-label="Ver detalle" style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--panel)', color: 'var(--ink-soft)', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center' }}><Icono nombre="ojo" size={15} /></button>
                      <button onClick={() => handlePDF(item._id, item.folio)} title="Descargar PDF" aria-label="Descargar PDF" style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--panel)', color: 'var(--ink-soft)', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center' }}><Icono nombre="descarga" size={15} /></button>
                      {puedeCorregir(item) && (
                        <button onClick={() => abrirCorregir(item)} title="Corregir y reenviar" aria-label="Corregir y reenviar" style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid var(--plasma-line)', background: 'var(--plasma-dim)', color: 'var(--plasma)', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center' }}><Icono nombre="refrescar" size={15} /></button>
                      )}
                      {['admin', 'contador'].includes(user.rol) && (
                        <button onClick={() => handleDelete(item._id)} title="Eliminar" aria-label="Eliminar" style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid var(--danger-line)', background: 'var(--danger-bg)', cursor: 'pointer', fontSize: 12, color: 'var(--danger)', display: 'inline-flex', alignItems: 'center' }}><Icono nombre="papelera" size={15} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <Paginacion page={filtros.page} pages={data.pages} onPage={p => setFiltros(f => ({ ...f, page: p }))} />
      </div>

      {/* Modal motivo de rechazo */}
      <Modal open={rechazoModal.open} onClose={() => setRechazoModal({ open: false, id: null, motivo: '' })} title="Motivo de Rechazo" width={420}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>Indica el motivo por el cual se rechaza esta comprobación.</div>
          <textarea autoFocus value={rechazoModal.motivo} onChange={e => setRechazoModal(m => ({ ...m, motivo: e.target.value }))}
            rows={3} placeholder="Ej: Falta documentación de soporte..."
            style={{ ...css.input(!rechazoModal.motivo.trim()), resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setRechazoModal({ open: false, id: null, motivo: '' })} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
            <button onClick={confirmarRechazo} style={{ ...css.btn('peligro'), fontSize: 13 }}>Confirmar rechazo</button>
          </div>
        </div>
      </Modal>

      {/* Modal corregir y reenviar */}
      <Modal open={!!corregirModal} onClose={() => setCorregirModal(null)} title={`Corregir y reenviar ${corregirModal?.folio || ''}`} width={560}>
        {corregirModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
            {corregirModal.motivoRechazo && (
              <div style={{ padding: '8px 12px', background: 'var(--danger-bg)', borderRadius: 8, border: '1px solid var(--danger-line)', color: 'var(--danger)', fontSize: 12.5 }}>
                <b>Motivo del rechazo:</b> {corregirModal.motivoRechazo}
              </div>
            )}
            <DestinoPagoFijo campos={[
              ['Comprobador', corregirModal.destino.comprobador],
              ['Banco', corregirModal.destino.banco], ['CLABE', corregirModal.destino.clabe],
              ['Cuenta', corregirModal.destino.cuenta],
            ]} />
            <Grid cols={2} gap={10}>
              <div><Label required>Monto</Label><MontoInput value={corregirModal.campos.monto} onChange={v => setCorregirModal(m => ({ ...m, campos: { ...m.campos, monto: v } }))} /></div>
              <div><Label>Concepto de gasto</Label><input value={corregirModal.campos.conceptoGasto} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, conceptoGasto: e.target.value } }))} style={css.input(false)} /></div>
              <div><Label>Folio CFDI</Label><input value={corregirModal.campos.folioCfdi} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, folioCfdi: e.target.value } }))} style={css.input(false)} /></div>
              <div><Label>Dirección</Label><input value={corregirModal.campos.direccion} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, direccion: e.target.value } }))} style={css.input(false)} /></div>
            </Grid>
            <div><Label>Observaciones</Label>
              <textarea value={corregirModal.campos.observaciones} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, observaciones: e.target.value } }))} rows={2} style={{ ...css.input(false), resize: 'vertical' }} /></div>
            <div><Label>Nota del reenvío (qué se corrigió)</Label>
              <input value={corregirModal.nota} onChange={e => setCorregirModal(m => ({ ...m, nota: e.target.value }))} placeholder="Ej: Se corrigió el monto" style={css.input(false)} /></div>
            <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>Si falta un documento, adjúntalo desde el detalle antes de reenviar. Al reenviar vuelve a quedar pendiente de aprobación.</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setCorregirModal(null)} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
              <button onClick={confirmarReenvio} disabled={corregirModal.enviando} style={{ ...css.btn('primario'), fontSize: 13, opacity: corregirModal.enviando ? 0.6 : 1 }}>
                {corregirModal.enviando ? 'Reenviando…' : 'Reenviar comprobación'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal comprobante de pago (obligatorio para marcar pagada) */}
      <Modal open={progModal.open} onClose={() => setProgModal({ open: false, id: null, fecha: '' })} title="Programar pago" width={420}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--glass)' }}>
            Indica la <b>fecha de programación</b> del pago. Aparecerá en el reporte, en Para Provisión y en el historial.
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: 'var(--ink-soft)' }}>
            <span style={{ fontWeight: 600 }}>Fecha de programación</span>
            <input type="date" value={progModal.fecha} onChange={e => setProgModal(m => ({ ...m, fecha: e.target.value }))} style={{ fontSize: 13, padding: '7px 10px', border: '1px solid var(--hair-strong)', borderRadius: 8 }} />
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setProgModal({ open: false, id: null, fecha: '' })} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
            <button onClick={confirmarProg} style={{ ...css.btn('primario'), fontSize: 13 }}><ConIcono nombre="calendario">Confirmar programación</ConIcono></button>
          </div>
        </div>
      </Modal>

      <Modal open={pagoModal.open} onClose={() => setPagoModal({ open: false, id: null, files: [], yaTiene: false, fecha: '', subiendo: false })} title="Marcar como Pagada" width={460}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--glass)' }}>
            Adjunta el comprobante de la transferencia o pago. Quedará guardado en los archivos con el concepto <b>"Comprobante de pago"</b>.
          </div>
          {pagoModal.yaTiene && (
            <div style={{ fontSize: 12, color: 'var(--ok)', background: 'var(--ok-bg)', border: '1px solid var(--ok-line)', borderRadius: 8, padding: '8px 12px' }}>
              <Icono nombre="check" size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />Ya tiene un comprobante de pago adjunto; puedes confirmar sin subir otro.
            </div>
          )}
          <input type="file" multiple accept=".pdf,.xml,.jpg,.jpeg,.png,.webp"
            onChange={e => setPagoModal(m => ({ ...m, files: Array.from(e.target.files || []) }))} style={{ fontSize: 12.5 }} />
          {pagoModal.files.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}><Icono nombre="clip" size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />{pagoModal.files.map(f => f.name).join(' · ')}</div>
          )}
          <SelectorCuentaPago empresa={pagoModal.empresa} value={pagoModal.cuentaPago} onChange={v => setPagoModal(m => ({ ...m, cuentaPago: v }))} />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: 'var(--ink-soft)' }}>
            <span style={{ fontWeight: 600 }}>Fecha de pago</span>
            <input type="date" value={pagoModal.fecha} max={new Date().toLocaleDateString('en-CA')} required
              onChange={e => setPagoModal(m => ({ ...m, fecha: e.target.value }))}
              style={{ fontSize: 13, padding: '7px 10px', border: '1px solid var(--hair-strong)', borderRadius: 8 }} />
            <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>La fecha en que salió el pago del banco (no la de hoy). Aparece en el reporte y mide cuánto tardamos en subir el comprobante.</span>
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setPagoModal({ open: false, id: null, files: [], yaTiene: false, fecha: '', subiendo: false })} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
            <button onClick={confirmarPago} disabled={pagoModal.subiendo} style={{ ...css.btn('primario'), fontSize: 13, opacity: pagoModal.subiendo ? 0.6 : 1 }}>
              {pagoModal.subiendo ? 'Subiendo…' : 'Confirmar pago'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal detalle */}
      {detalle && (
        <Modal open={!!detalle} onClose={() => setDetalle(null)} title={`Comprobación ${detalle.folio}`} width={600} zIndex={900}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Alerta de presupuesto del centro de costos */}
            {rebasados[detalle.centroCostos] && (
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--danger-bg)', border: '1px solid var(--danger-line)', color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>
                <ConIcono nombre="alerta" size={14}>{alertaPresupuesto(detalle.centroCostos)}</ConIcono>
              </div>
            )}
            <div style={{ display: 'grid', gap: 0 }}>
              {[
                ['Compañía', detalle.empresa], ['Sucursal', detalle.sucursal],
                ['Centro de Costos', detalle.centroCostos], ['Dirección', detalle.direccion],
                ['Comprobador', detalle.comprobador], ['Concepto de Gasto', detalle.conceptoGasto],
                ['Monto', `$${(detalle.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} ${detalle.moneda}`],
                ['Concepto del Pago', detalle.observaciones],
                ['Solicitante', detalle.usuario?.nombre || '—'],
                ['Fecha', detalle.fechaSolicitud || new Date(detalle.createdAt).toLocaleDateString('es-MX')],
                ['Estado', ESTADO_LABEL[detalle.estado] || detalle.estado], ['Pagada desde', detalle.cuentaPago ? [detalle.cuentaPago.banco, detalle.cuentaPago.terminacion && `••${detalle.cuentaPago.terminacion}`].filter(Boolean).join(' ') : null],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--hair-soft)' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.04em', width: 140, flexShrink: 0 }}>{label}</span>
                  <span style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>{value}</span>
                </div>
              ))}
              {detalle.motivoRechazo && (
                <div style={{ display: 'flex', gap: 12, padding: '8px', background: 'var(--danger-bg)', borderRadius: 6, marginTop: 4 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--danger)', width: 140, flexShrink: 0, fontWeight: 700, textTransform: 'uppercase' }}>Motivo rechazo</span>
                  <span style={{ color: 'var(--danger)', fontWeight: 500, fontSize: 13 }}>{detalle.motivoRechazo}</span>
                </div>
              )}
            </div>

            {detalle.historialEstados?.length > 0 && (
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Historial de estados</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {detalle.historialEstados.map((h, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11.5, fontWeight: 700, background: `${ESTADO_COLORS[h.estado]}18`, color: ESTADO_COLORS[h.estado], border: `1px solid ${ESTADO_COLORS[h.estado]}40`, whiteSpace: 'nowrap' }}>{ESTADO_LABEL[h.estado] || h.estado}</span>
                      <span style={{ color: 'var(--glass)' }}>{h.nombreUsuario}</span>
                      {h.motivo && <span style={{ color: 'var(--glass)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{h.motivo}"</span>}
                      <span style={{ color: 'var(--glass)', marginLeft: 'auto', whiteSpace: 'nowrap', fontSize: 11.5 }}>{new Date(h.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Comentarios {detalle.comentarios?.length > 0 && `(${detalle.comentarios.length})`}
              </div>
              {detalle.comentarios?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10, maxHeight: 180, overflowY: 'auto' }}>
                  {detalle.comentarios.map((c, i) => (
                    <div key={i} style={{ background: 'var(--panel)', borderRadius: 8, padding: '8px 12px', border: '1px solid var(--hair)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>{c.nombre}</span>
                          <span style={{ fontSize: 11.5, padding: '1px 6px', borderRadius: 4, background: ROL_COLORS[c.rol] ? `${ROL_COLORS[c.rol]}18` : 'var(--panel-hi)', color: ROL_COLORS[c.rol] || 'var(--glass)', fontWeight: 600, border: `1px solid ${ROL_COLORS[c.rol] ? `${ROL_COLORS[c.rol]}25` : 'var(--mesh-soft)'}` }}>{c.rol}</span>
                        </div>
                        <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>{new Date(c.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{c.texto}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--glass)', marginBottom: 8 }}>Sin comentarios aún.</div>
              )}
              {user?.rol !== 'director' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <input ref={comentarioRef} value={comentario} onChange={e => setComentario(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComentario(); } }}
                  placeholder="Escribe un comentario... (Enter para enviar)"
                  style={{ ...css.input(false), flex: 1, fontSize: 12.5 }} />
                <button onClick={handleComentario} disabled={!comentario.trim() || enviandoComentario}
                  style={{ ...css.btn('primario'), fontSize: 12, padding: '0 16px', flexShrink: 0 }}>
                  {enviandoComentario ? '...' : 'Enviar'}
                </button>
              </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => handlePDF(detalle._id, detalle.folio)} style={css.btn('secundario')}><ConIcono nombre="descarga">Descargar PDF</ConIcono></button>
              <button onClick={() => setDetalle(null)} style={css.btn('secundario')}>Cerrar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
